import Table from "@/ui/table-div.tsx";
import TableBase from "@/ui/table";
import {
  Age,
  Index,
  Inflow,
  Outflow,
  Pnl,
  PnlInPer,
  Price,
  Value,
} from "@/ui/formats.tsx";
import { IconComponent } from "@/ui/icon";
import { Link } from "react-router";
import IconButton from "@/ui/icon-button";
import { Token } from "@/ui/token";
import {
  BOT_GET_ORDERS_QUERY_KEY,
  useGetBotOrders,
} from "@/api/bot/bot-get-orders";
import { CodexChainId, useWallet, WalletType } from "@/providers/wallet";
import { LOGO_URL } from "@/constants";
import {
  BotRemoveOrderParams,
  useBotRemoveOrderMutation,
} from "@/api/bot/bot-remove-order";
import { useQueryClient } from "@tanstack/react-query";
import { useOrdersFilter } from "@/providers/orders-filter/orders-filter";
import { useMemo } from "react";
import { TokenValueToggleView } from "@/features/token-value-toggle/token-value-toggle";

const OrdersTableData = ({
  selectedView,
}: {
  selectedView: TokenValueToggleView;
}) => {
  const { userId, appPubKey, currentWallet } = useWallet();

  const chainId = currentWallet.botChainId;
  const walletAddress = currentWallet.address;

  const chain =
    currentWallet.walletType === WalletType.ETH
      ? CodexChainId.ETH
      : CodexChainId.SOL;

  const queryClient = useQueryClient();

  const { data, isLoading, isRefetching, isError } = useGetBotOrders({
    userId,
    appPubKey,
    chainId,
    walletAddress,
  });

  const { filterOrders, handleTokenChange } = useOrdersFilter();

  const orders = useMemo(() => filterOrders(data ?? []), [data, filterOrders]);

  const { mutate } = useBotRemoveOrderMutation();

  const handleDeleteOrder = (orderId: string) => {
    const orderToDelete: BotRemoveOrderParams = {
      appPubKey: appPubKey,
      userId: userId,
      chainId: chainId,
      orderId: orderId,
      walletAddress: walletAddress,
    };

    mutate(orderToDelete, {
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: [BOT_GET_ORDERS_QUERY_KEY],
          exact: false,
        });
      },
    });
  };

  const chainIcon = () => {
    return (
      <IconComponent
        name={currentWallet.walletType}
        width="1rem"
        height="1rem"
      />
    );
  };

  return (
    <>
      {isLoading || isRefetching ? (
        <Table.Row>
          <div className="w-full h-[0.0625rem] bg-black overflow-hidden">
            <div className="w-full h-full bg-[#5AC4BE] animate-[loading_1s_ease-in-out_infinite]" />
          </div>
        </Table.Row>
      ) : isError ? (
        <TableBase.Row>
          <TableBase.Column className="py-[0.425rem] px-[2.1rem] text-xs">
            Something went wrong while loading your wallet orders. Please try
            again later.
          </TableBase.Column>
        </TableBase.Row>
      ) : orders?.length === 0 ? (
        <TableBase.Row>
          <TableBase.Column className="py-[0.425rem] px-[2.1rem] text-xs">
            No orders found for this wallet.
          </TableBase.Column>
        </TableBase.Row>
      ) : (
        orders?.map((order, index: number) => {
          const tokenLogo = `${LOGO_URL}/${currentWallet.walletType}/${order.tokenInfo?.address}.jpg`;
          const textColorClass =
            order.type === "sell" ? "text-red2" : "text-green2";

          return (
            <Table.Row
              key={order.orderId + index}
              className="h-[2rem] relative gradient-box before:py-[1px]
            before:pl-[1px] before:hover:bg-gradient-100 hover:bg-table-hover
            text-xs z-0 flex items-center"
            >
              <Table.Column className="w-[2.4%] flex justify-center items-center font-semibold">
                <Index value={index + 1} />
              </Table.Column>
              <Table.Column className="w-[28.3%] pl-1">
                <div className="flex items-center">
                  <IconComponent
                    name={currentWallet.walletType}
                    className="h-[1.1875rem] w-[1.1875rem] shrink-0"
                  />
                  <div className="flex-row flex gap-x-[0.3125rem] items-center text-xs">
                    <Token
                      name={order.tokenInfo?.name || ""}
                      imageUrl={tokenLogo}
                      symbol={order.tokenInfo?.symbol || ""}
                    />
                    <div className="flex items-center gap-x-[0.188rem]">
                      <IconButton
                        withHover={true}
                        withBorder={false}
                        name="search"
                        variant="circle"
                        onClick={() =>
                          handleTokenChange(order.tokenInfo?.address)
                        }
                        className="w-[0.938rem] h-[0.938rem] bg-black rounded-full flex justify-center items-center"
                        iconClassName="w-[65%] h-[65%]"
                      />
                    </div>
                    <Link
                      to={`/screener/${order.tokenInfo?.address}:${chain}`}
                      target="_blank"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <IconButton
                        withHover={true}
                        withBorder={false}
                        name="barChart"
                        variant="circle"
                        className="w-[0.938rem] h-[0.938rem] bg-black rounded-full flex justify-center items-center"
                        iconClassName="w-[60%] h-[60%]"
                      />
                    </Link>
                  </div>
                </div>
              </Table.Column>
              <Table.Column
                className={`w-[10.155%] capitalize ${textColorClass}`}
              >
                {order.type}
              </Table.Column>
              <Table.Column className="w-[10.155%]">
                <Price value={Number(order.price)} decimals={7} />
              </Table.Column>
              <Table.Column className="w-[10.155%]">
                {order.type === "buy" ? (
                  <Inflow value={Number(order.inflow ?? 0)} />
                ) : (
                  <Outflow value={Number(order.outflow ?? 0)} />
                )}
              </Table.Column>
              {selectedView === "sol" ? (
                <Table.Column className="w-[10.155%]">
                  {order.inflow ? (
                    <div className="flex items-center gap-2">
                      {chainIcon()}
                      {order.type === "buy" ? (
                        <Outflow value={Number(order.outflow ?? 0)} />
                      ) : (
                        <Inflow value={Number(order.inflow ?? 0)} />
                      )}
                    </div>
                  ) : (
                    <div>-</div>
                  )}
                </Table.Column>
              ) : (
                <Table.Column className="w-[10.155%]">
                  <Value value={Number(order.value ?? 0)} withSign={false} />
                </Table.Column>
              )}
              <Table.Column className="w-[10.155%]">
                <Pnl value={order.expectedPnl ?? 0} />
              </Table.Column>
              <Table.Column className="w-[10.155%]">
                <PnlInPer value={order.expectedRoi ?? 0} />
              </Table.Column>
              <Table.Column className="w-[8%]">
                <Age value={order.createdAt as number} suffix=" ago" />
              </Table.Column>
              <Table.Column className="w-[8%] flex justify-center">
                <IconButton
                  name="close"
                  onClick={() => handleDeleteOrder(order.orderId)}
                  className="h-[1.188rem] w-[1.188rem] bg-black"
                  iconClassName="w-[40%] h-[40%]"
                  withBorder={false}
                />
              </Table.Column>
            </Table.Row>
          );
        })
      )}
    </>
  );
};

const OrdersTable = ({
  selectedView,
}: {
  selectedView: TokenValueToggleView;
}) => {
  const { currentWallet } = useWallet();

  const { sort, handleSortChange } = useOrdersFilter();

  const renderSortIcon = (column: string) => {
    if (sort.sortField === column) {
      return (
        <IconComponent
          className="w-[0.375rem] h-[0.375rem]"
          name={sort.sortType === "desc" ? "arrowDown" : "arrowUp"}
          color="white"
        />
      );
    }
    return null;
  };

  return (
    <Table className="h-[calc(100vh-7rem)] text-left">
      <Table.Header className="h-[1.5625rem] bg-black items-center">
        <Table.HeaderColumn className="w-[2.4%] flex justify-center items-center">
          #
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[28.3%] pl-1 cursor-pointer flex items-center gap-1"
          onClick={() => handleSortChange("token")}
        >
          Token
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon("token")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[10.155%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortChange("type")}
        >
          Type
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon("type")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[10.155%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortChange("price")}
        >
          Price
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon("price")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[10.155%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortChange("outflow")}
        >
          Amount
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon("outflow")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[10.155%] cursor-pointer flex items-center gap-1"
          onClick={() =>
            handleSortChange(selectedView === "sol" ? "inflow" : "value")
          }
        >
          {selectedView === "sol" ? "SOL" : "Value"}
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon(selectedView === "sol" ? "inflow" : "value")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[10.155%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortChange("realizedPNL")}
        >
          Realized PNL
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon("realizedPNL")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[10.155%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortChange("realizedROI")}
        >
          Realized ROI
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon("realizedROI")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[8%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortChange("time")}
        >
          Time
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {renderSortIcon("time")}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn className="w-[8%] text-center">
          Cancel
        </Table.HeaderColumn>
      </Table.Header>
      <Table.Body>
        {!currentWallet ? (
          <TableBase.Row>
            <TableBase.Column className="py-[0.425rem] px-[2.1rem] text-xs">
              Sign in to view data
            </TableBase.Column>
          </TableBase.Row>
        ) : (
          <OrdersTableData selectedView={selectedView} />
        )}
      </Table.Body>
    </Table>
  );
};

export default OrdersTable;
